"use client";

import Image from "next/image";
import { ArticleCard } from "@/components/blog/article-card";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { getBlogPostImage } from "@/lib/blog-images";
import { buildLocalizedPublicPath } from "@/lib/i18n/public-routing";
import { type SupportedLanguage } from "@/lib/languages";
import { cn } from "@/lib/utils";
import { ArrowRight, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { reportWarning } from "@/lib/observability/report-error";
import { formatDate as safeFormatDate } from "@/lib/utils/format";

interface BlogPostMetadata {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  readTime: string;
  tags: string[];
  keywords: string[];
  relatedArticles?: string[];
  lang: string;
}

interface BlogListingClientProps {
  posts: BlogPostMetadata[];
  locale: SupportedLanguage;
  translations: {
    postOverline: string;
    heroTitle: string;
    heroSubtitle: string;
    filtersLabel: string;
    filtersAll: string;
    readArticle: string;
    noFiltered: string;
    noPosts: string;
    ctaTitle: string;
    ctaDescription: string;
    ctaViewPricing: string;
    ctaGetStarted: string;
    paginationPrevious: string;
    paginationNext: string;
  };
}

const PAGE_SIZE = 12;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

function formatDate(dateString: string, locale: string): string {
  try {
    return safeFormatDate(dateString, dateString, locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    reportWarning(error, {
      source: "components.BlogListingClient",
      feature: "blog",
      action: "formatDate",
    });
    return dateString;
  }
}

export function BlogListingClient({
  posts,
  locale,
  translations: t,
}: BlogListingClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const blogIndexPath = buildLocalizedPublicPath("/blog", locale);

  const allTags = useMemo(() => {
    const tagCount = new Map<string, number>();
    posts.forEach((post) =>
      post.tags?.forEach((tag) =>
        tagCount.set(tag, (tagCount.get(tag) || 0) + 1)
      )
    );
    return Array.from(tagCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag]) => tag);
  }, [posts]);

  const filteredPosts = useMemo(
    () =>
      activeTag ? posts.filter((p) => p.tags?.includes(activeTag)) : posts,
    [posts, activeTag]
  );

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const parsedPage = useMemo(() => {
    const raw = searchParams.get("page");
    const numeric = raw ? Number.parseInt(raw, 10) : 1;
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
  }, [searchParams]);
  const currentPage = clamp(parsedPage, 1, totalPages);
  const visiblePosts = filteredPosts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const featuredPost = currentPage === 1 && !activeTag ? visiblePosts[0] : null;
  const gridPosts = featuredPost ? visiblePosts.slice(1) : visiblePosts;

  const handlePageChange = (page: number) => {
    const targetPage = clamp(page, 1, totalPages);
    const params = new URLSearchParams(searchParams.toString());
    if (targetPage <= 1) params.delete("page");
    else params.set("page", String(targetPage));
    const query = params.toString();
    router.push(query ? `${blogIndexPath}?${query}` : blogIndexPath);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleTagChange = (nextTag: string | null) => {
    setActiveTag(nextTag);
    // Reset pagination when filters change to avoid stale page params.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("page");
    const query = params.toString();
    router.push(query ? `${blogIndexPath}?${query}` : blogIndexPath);
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-12 pt-24 sm:py-16">
      {/* Hero */}
      <header className="mb-12">
        <p className="text-leaf-600 dark:text-leaf-400 mb-3 text-xs font-semibold tracking-[0.2em] uppercase">
          {t.postOverline}
        </p>
        <h1 className="text-foreground mb-3 text-3xl leading-[1.08] font-extrabold tracking-tight sm:text-5xl">
          {t.heroTitle}
        </h1>
        <p className="text-muted-foreground max-w-xl text-base leading-relaxed">
          {t.heroSubtitle}
        </p>
      </header>

      {/* Tag Filter Bar */}
      {allTags.length > 0 && (
        <nav
          className="mb-12 flex flex-wrap items-center gap-2"
          aria-label={t.filtersLabel}
        >
          <button
            type="button"
            onClick={() => handleTagChange(null)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200",
              !activeTag
                ? "border-foreground bg-foreground text-background"
                : "border-border hover:border-border hover:text-foreground bg-muted text-muted-foreground dark:bg-transparent"
            )}
          >
            {t.filtersAll}
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => handleTagChange(activeTag === tag ? null : tag)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200",
                activeTag === tag
                  ? "border-foreground bg-foreground text-background"
                  : "border-border hover:border-border hover:text-foreground bg-muted text-muted-foreground dark:bg-transparent"
              )}
            >
              {tag}
            </button>
          ))}
        </nav>
      )}

      {/* Featured Post */}
      {featuredPost && (
        <Link
          href={`/blog/${featuredPost.slug}`}
          className="group mb-14 block overflow-visible whitespace-normal"
        >
          <article className="border-border/50 hover:border-border dark:bg-foreground/[0.02] bg-card relative overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:shadow-2xl hover:shadow-black/30 dark:shadow-none dark:hover:shadow-black/30">
            <div className="grid grid-cols-1 lg:grid-cols-5">
              <div className="relative aspect-[16/10] overflow-hidden rounded-t-2xl lg:col-span-3 lg:aspect-auto lg:rounded-l-2xl lg:rounded-tr-none">
                <Image
                  src={getBlogPostImage(featuredPost.slug)}
                  alt={featuredPost.title}
                  fill
                  className="blog-card-image object-cover"
                  unoptimized
                  priority
                />
                <div className="absolute inset-0 hidden bg-gradient-to-r from-transparent to-black/40 lg:block" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent lg:hidden" />
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-10 lg:col-span-2">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  {featuredPost.tags?.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="text-leaf-600 dark:text-leaf-400/80 text-[11px] font-semibold tracking-widest uppercase"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h2
                  className="text-foreground group-hover:text-leaf-600 dark:group-hover:text-leaf-300 mb-4 text-2xl font-extrabold transition-colors sm:text-3xl lg:text-4xl"
                  style={{
                    display: "block",
                    overflow: "visible",
                    textOverflow: "unset",
                    whiteSpace: "normal",
                    wordBreak: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: "1.3",
                  }}
                >
                  {featuredPost.title}
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3 text-sm leading-relaxed sm:text-base">
                  {featuredPost.description}
                </p>
                <div className="text-muted-foreground/70 mb-6 flex items-center gap-4 text-xs">
                  <time>{formatDate(featuredPost.publishedAt, locale)}</time>
                  <span>&middot;</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {featuredPost.readTime}
                  </span>
                </div>
                <span className="text-leaf-600 dark:text-leaf-400 inline-flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-3">
                  {t.readArticle}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </article>
        </Link>
      )}

      {/* Empty state */}
      {gridPosts.length === 0 && !featuredPost && (
        <div className="py-24 text-center">
          <p className="text-muted-foreground text-lg">
            {activeTag ? t.noFiltered : t.noPosts}
          </p>
        </div>
      )}

      {/* Posts Grid */}
      {gridPosts.length > 0 && (
        <section className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {gridPosts.map((post) => (
            <ArticleCard
              key={`${post.slug}-${post.lang}`}
              slug={post.slug}
              title={post.title}
              description={post.description}
              date={post.publishedAt}
              readTime={post.readTime}
              tags={post.tags}
            />
          ))}
        </section>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mb-20 flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="hover:bg-foreground/10 hover:text-foreground text-muted-foreground h-9 w-9 rounded-full disabled:opacity-30"
            aria-label={t.paginationPrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                type="button"
                className={cn(
                  "h-9 w-9 rounded-full text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-foreground text-background"
                    : "hover:bg-foreground/10 hover:text-foreground text-muted-foreground/70"
                )}
                onClick={() => handlePageChange(page)}
                aria-current={isActive ? "page" : undefined}
              >
                {page}
              </button>
            );
          })}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="hover:bg-foreground/10 hover:text-foreground text-muted-foreground h-9 w-9 rounded-full disabled:opacity-30"
            aria-label={t.paginationNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* CTA Section */}
      <div className="border-border from-leaf-500/10 relative overflow-hidden rounded-2xl border bg-gradient-to-br via-transparent to-green-500/10 p-10 text-center sm:p-14">
        <div className="from-leaf-500 absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r to-green-500" />
        <h2 className="text-foreground mb-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
          {t.ctaTitle}
        </h2>
        <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-base leading-relaxed sm:text-lg">
          {t.ctaDescription}
        </p>
        <div className="flex flex-col justify-center gap-4 sm:flex-row">
          <Link href="/seeds">
            <Button
              size="lg"
              className="bg-foreground text-background shadow-foreground/10 hover:bg-foreground/90 h-12 px-8 text-base font-semibold shadow-lg"
            >
              {t.ctaViewPricing}
            </Button>
          </Link>
          <Link href="/getting-started">
            <Button
              size="lg"
              variant="outline"
              className="border-border text-foreground hover:bg-foreground/10 h-12 px-8 text-base font-semibold"
            >
              {t.ctaGetStarted}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
